import { contactCategoryKey, CONTACT_CATEGORY_OTHER_LABEL } from './DefaultContactCategoryMapColors';
import { canonicalTitleStatusValue, NO_TITLE_LABEL } from './DefaultTitleStatusMapColors';
import { IContractLayerFields } from './ContractLayers';

/*
 * What a tract's color means on the active layer, for the map's linger-to-peek tooltip: one
 * swatch per category the tract is drawn with, so a combination tract (drawn hatched) gets one
 * swatch per piece. Built from the same keys the layers' renderers compute and the same colors
 * the side panel's swatches use (alpha = layer opacity), so the tooltip matches the map.
 */

export interface IHoverSwatch {
    label: string;
    rgba: number[] | undefined;
}

export type TractAttributes = Record<string, unknown>;

/* A tract's hover description on one layer; registered per layer id as each renderer is built. */
export type HoverDescriber = (attributes: TractAttributes) => IHoverSwatch[];

/* A renderer key's pieces: 'Committed | Working' -> ['Committed', 'Working']. */
const keyPieces = (key: string): string[] =>
    key
        .split('|')
        .map((piece) => piece.trim())
        .filter((piece) => piece.length > 0);

/* Contact Category layer: each category the tract carries, folded the way the renderer folds them. */
export const contactCategorySwatches = (attributes: TractAttributes, colors: Record<string, number[]>): IHoverSwatch[] =>
    keyPieces(contactCategoryKey(String(attributes.Contact_Category ?? ''))).map((label) => ({
        label,
        rgba: colors[label] ?? colors[CONTACT_CATEGORY_OTHER_LABEL],
    }));

/* Title Status layer: the tract's status. Anything the legend has no row for is drawn in the grey
 * 'No Title' default, so that's its swatch. */
export const titleStatusSwatches = (attributes: TractAttributes, colors: Record<string, number[]>, noTitleRgba: number[]): IHoverSwatch[] =>
    keyPieces(canonicalTitleStatusValue(attributes.Title_Status)).map((label) => ({
        label,
        rgba: label === NO_TITLE_LABEL ? noTitleRgba : colors[label] ?? noTitleRgba,
    }));

/* Leasehold and contract layers: the bands the tract is drawn with. Mirrors
 * contractLayerValueExpression: the composition when present, else the client flag, else the
 * WI owner(s), else unleased. Owners outside the layer's palette draw in the grey 'Other' color
 * but keep their own name here, which is the point of peeking. */
export const compositionSwatches = (
    attributes: TractAttributes,
    fields: IContractLayerFields,
    colors: Record<string, number[]>,
    clientLabel: string
): IHoverSwatch[] => {
    const composition = String(attributes[fields.composition] ?? '').trim();
    const clientOwned = attributes[fields.clientOwned];
    let key: string;
    if (composition !== '') {
        key = composition;
    } else if (clientOwned === 1 || clientOwned === '1' || clientOwned === true) {
        key = '__CLIENT__';
    } else {
        const owner = String(attributes[fields.owner] ?? '').trim();
        key = owner === '' || owner === 'Unleased' ? '__UNLEASED__' : owner;
    }
    return keyPieces(key).map((token) => ({
        label: token === '__CLIENT__' ? clientLabel : token === '__UNLEASED__' ? 'Unleased' : token,
        rgba: colors[token] ?? colors.__OTHER__,
    }));
};

/* Where the peek sits as it follows the cursor: below and right of it, flipped to the cursor's other
 * side on either axis where it would run past the map's edge, and never off its top or left. */
export const hoverTipPosition = (
    point: { x: number; y: number },
    tip: { width: number; height: number },
    area: { width: number; height: number },
    offset = 14
): { left: number; top: number } => ({
    left: point.x + offset + tip.width > area.width ? Math.max(0, point.x - offset - tip.width) : point.x + offset,
    top: point.y + offset + tip.height > area.height ? Math.max(0, point.y - offset - tip.height) : point.y + offset,
});

/* CSS color for a swatch; transparent when a layer reported no color for it. */
export const swatchCss = (rgba: number[] | undefined): string => (rgba ? `rgba(${rgba[0]}, ${rgba[1]}, ${rgba[2]}, ${rgba[3] ?? 1})` : 'transparent');

/*
 * Which tract the peek describes, by KuduTractID. A selected tract wins wherever the mouse is
 * inside it, even where another tract draws on top of it (overlap cycling can select one
 * underneath), and the tract open in the panel (the clicked one) comes first. Anywhere else it's
 * the topmost tract, the one whose color shows.
 */

/* Whether the selected tracts under the mouse need looking up: only when one of them could win
 * over the topmost tract. */
export const peekNeedsSelectedLookup = (topmostId: string, clickedId: string | null, selectedIds: string[]): boolean =>
    clickedId ? topmostId !== clickedId : selectedIds.length > 0 && !selectedIds.includes(topmostId);

/* The tract to describe, given which selected tracts are under the mouse. */
export const peekTractId = (topmostId: string, clickedId: string | null, selectedIds: string[], selectedUnderMouse: string[]): string => {
    if (clickedId && selectedUnderMouse.includes(clickedId)) {
        return clickedId;
    }
    if (topmostId === clickedId || selectedIds.includes(topmostId)) {
        return topmostId;
    }
    return selectedUnderMouse[0] ?? topmostId;
};



// WEBPACK FOOTER //
// ./src/components/project/sections/GISComponents/tractHoverTip.ts